import type { MyApplication, MyEntry, MySignup } from '../../api/myActivity'

// The account hub's Competitions page: what a player's teams entered, what
// they signed up for or applied to on their own, and what's finished.

const LIVE_SEASON = new Set(['open', 'active', 'playoffs'])
const LIVE_ENTRY = new Set(['pending', 'approved', 'active'])
const LIVE_SIGNUP = new Set(['pending', 'open'])
const LIVE_APPLICATION = new Set(['pending', 'tryout'])

/** An entry still in play: the competition hasn't ended and the team is in or waiting. */
export function isLiveEntry(e: MyEntry): boolean {
  return LIVE_SEASON.has(e.status) && LIVE_ENTRY.has(e.registration_status)
}

export function isLiveSignup(s: MySignup): boolean {
  return LIVE_SIGNUP.has(s.status)
}

export function isLiveApplication(a: MyApplication): boolean {
  return LIVE_APPLICATION.has(a.status)
}

/** Live entries first by start date; then the rest, most recent first. */
export function splitEntries(entries: MyEntry[]): { live: MyEntry[]; past: MyEntry[] } {
  const time = (e: MyEntry) => (e.starts_at ? new Date(e.starts_at).getTime() : Number.MAX_SAFE_INTEGER)
  const live = entries.filter(isLiveEntry).sort((a, b) => time(a) - time(b) || a.name.localeCompare(b.name))
  const past = entries.filter((e) => !isLiveEntry(e)).sort((a, b) => time(b) - time(a))
  return { live, past }
}

/** Entries whose team still has to agree to the competition's latest rules. */
export function rulesOwed(entries: MyEntry[]): MyEntry[] {
  return entries.filter((e) => isLiveEntry(e) && e.rules_to_agree > 0)
}

/** Where a competition lives in its league's pages; `tab` opens one of its tabs. */
export function competitionPath(e: Pick<MyEntry, 'league_slug' | 'slug'>, tab?: string): string {
  return `/org/${e.league_slug}/competitions/${e.slug}${tab ? `/${tab}` : ''}`
}

/** The entry's state in a few words: "Starts Dec 5", "In progress", "Waiting for approval". */
export function entryStatus(e: MyEntry, now = new Date()): { label: string; tone: 'live' | 'waiting' | 'done' } {
  switch (e.registration_status) {
    case 'pending': return { label: 'Waiting for approval', tone: 'waiting' }
    case 'rejected': return { label: 'Not accepted', tone: 'done' }
    case 'withdrawn': return { label: 'Withdrawn', tone: 'done' }
  }
  switch (e.status) {
    case 'completed': return { label: 'Finished', tone: 'done' }
    case 'cancelled': return { label: 'Cancelled', tone: 'done' }
    case 'playoffs': return { label: 'Playoffs', tone: 'live' }
    case 'active': return { label: 'In progress', tone: 'live' }
  }
  if (e.starts_at && new Date(e.starts_at) > now) {
    return { label: `Starts ${shortDate(e.starts_at)}`, tone: 'waiting' }
  }
  return { label: 'Open', tone: 'live' }
}

/** "Captain", "Player", "Owner" — the player's part in the entry. */
export function roleLabel(role: string): string {
  return role ? role.charAt(0).toUpperCase() + role.slice(1) : 'Player'
}

export function shortDate(iso: string): string {
  return new Date(iso).toLocaleDateString(undefined, { month: 'short', day: 'numeric' })
}

/** "Waiting", "Tryout", "Accepted"… for an application or a signup. */
export function applicationStatus(status: string): string {
  return ({
    pending: 'Waiting', tryout: 'Tryout', approved: 'Accepted', accepted: 'Accepted',
    rejected: 'Declined', declined: 'Declined', withdrawn: 'Withdrawn',
    open: 'Listed', placed: 'Placed on a team', expired: 'Expired',
  } as Record<string, string>)[status] ?? status
}
